import React, { useState } from 'react';
import { GlassIcon } from '../common/GlassIcon';
import { createPaymentRequest, validateCenterCode, useCenterCode } from '../../services/d1Api';

export const CoursePurchaseModal = ({ course, onClose, user, userData, onSuccess }) => {
  const [step, setStep] = useState(1); // 1: details, 2: payment methods, 3: code entry
  const [paymentMethod, setPaymentMethod] = useState('vodafone');
  const [receiptImage, setReceiptImage] = useState('');
  const [transactionId, setTransactionId] = useState('');
  const [centerCode, setCenterCode] = useState('');
  const [codeValidating, setCodeValidating] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const teacherNumber = '010xxxxxxxx'; // Replace with actual number
  const vodafoneCashNumber = '01044803792';
  const instapayUsername = 'قريباً';

  // Detect free course
  const isFree = !course.price || Number(course.price) === 0 || course.price === '0';

  // FIX: The backend stores the image as image_url; fall back to image for safety.
  const courseImage = course.image_url || course.image;

  const handleCodeSubmit = async () => {
    if (!centerCode.trim()) {
      setCodeError('الرجاء إدخال الكود');
      return;
    }

    setCodeValidating(true);
    setCodeError('');

    const result = await validateCenterCode(centerCode.toUpperCase());

    if (!result.valid) {
      setCodeError(result.message || 'كود غير صالح');
      setCodeValidating(false);
      return;
    }

    // FIX: d1Api.validateCenterCode spreads result.data onto the returned object,
    // so course_id is at the top level of `result`, not nested under result.data.
    if (result.course_id !== course.id) {
      setCodeError('هذا الكود غير صالح لهذا الكورس');
      setCodeValidating(false);
      return;
    }

    // Use the code
    const useResult = await useCenterCode(centerCode.toUpperCase(), user.uid || user.id);

    if (useResult.success) {
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } else {
      setCodeError(useResult.error || useResult.message || 'حدث خطأ أثناء تفعيل الكود');
    }

    setCodeValidating(false);
  };

  // Free course enrollment — approved instantly
  const handleFreeEnroll = async () => {
    setLoading(true);
    const operationId = `FREE-${Math.floor(Math.random() * 1000000)}`;

    const result = await createPaymentRequest({
      user_id: user.id,
      course_id: course.id,
      amount: 0,
      status: 'approved',
      operationId: operationId
    });

    if (result.success) {
      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } else {
      alert('حدث خطأ أثناء التسجيل: ' + result.error);
    }

    setLoading(false);
  };

  const handlePaymentSubmit = async () => {
    setLoading(true);

    const operationId = `REQ-${Math.floor(Math.random() * 1000000)}`;

    // 1. Create the payment request in the database first
    const result = await createPaymentRequest({
      user_id: user.id, // D1 schema uses `id`
      course_id: course.id,
      amount: Number(course.price) || 0,
      status: 'pending',
      operationId: operationId
    });

    if (result.success) {
      // 2. If successful, open WhatsApp
      const phoneNumber = "201044803792"; // Replace if you have a specific number
      const message = `طلب اشتراك في كورس: ${course.title}\nرقم العملية: ${operationId}\nالرجاء إرسال صورة إيصال الدفع هنا.`;
      const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

      window.open(whatsappUrl, '_blank');

      setSuccess(true);
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 2000);
    } else {
      alert('حدث خطأ أثناء تقديم الطلب: ' + result.error);
    }

    setLoading(false);
  };

  if (success) {
    return (
      <div className="fixed inset-0 bg-black/95 z-[300] flex items-center justify-center p-4">
        <div className="glass-panel p-8 max-w-md w-full text-center">
          <div className={`w-20 h-20 ${isFree ? 'bg-green-500/20' : 'bg-green-500/20'} rounded-full flex items-center justify-center mx-auto mb-4`}>
            <GlassIcon name="check-circle" size={40} className="text-green-500" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">
            {isFree ? 'تم الاشتراك بنجاح!' : 'تم إرسال الطلب بنجاح!'}
          </h3>
          <p className="text-slate-400">
            {isFree
              ? 'تم تفعيل الكورس تلقائياً، يمكنك البدء الآن!'
              : 'سيتم تفعيل الكورس بعد مراجعة الإدارة'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/95 z-[300] flex items-center justify-center p-4" onClick={onClose}>
      <div className="glass-panel p-6 w-full max-w-lg relative animate-scale-in" onClick={e => e.stopPropagation()}>
        <button onClick={onClose} className="absolute top-4 left-4 p-2 hover:bg-white/10 rounded-full">
          <GlassIcon name="x" size={20} />
        </button>

        {step === 1 && (
          <div className="space-y-6">
            {/* FIX: Use courseImage which resolves image_url with fallback to image */}
            <div className="h-48 rounded-xl overflow-hidden relative">
              <img src={courseImage} className="w-full h-full object-cover" alt={course.title} />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent flex items-end p-4">
                <h2 className="text-2xl font-bold text-white">{course.title}</h2>
              </div>
            </div>

            <div className="space-y-4">
              <div className="bg-white/5 p-4 rounded-xl">
                <h3 className="font-bold text-brand-accent mb-2">مميزات الكورس:</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{course.description}</p>
              </div>

              <div className="flex justify-between items-center text-sm font-bold">
                <span className="bg-blue-500/20 text-blue-300 px-3 py-1 rounded-lg">{course.duration} يوم</span>
                {isFree ? (
                  <span className="text-2xl text-green-400 flex items-center gap-2">
                    <GlassIcon name="gift" size={22} className="text-green-400" />
                    مجاناً
                  </span>
                ) : (
                  <span className="text-3xl text-green-400">{course.price} <span className="text-sm">ج.م</span></span>
                )}
              </div>
            </div>

            {isFree ? (
              /* Free course: single enroll button */
              <button
                onClick={handleFreeEnroll}
                disabled={loading}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-3 transition-all hover:scale-[1.02] shadow-lg shadow-green-500/20 disabled:opacity-50"
              >
                {loading ? (
                  'جاري التسجيل...'
                ) : (
                  <>
                    <GlassIcon name="check-circle" size={22} />
                    اشترك الآن مجاناً
                  </>
                )}
              </button>
            ) : (
              /* Paid course: payment methods + center code buttons */
              <div className="flex gap-3">
                <button onClick={() => setStep(2)} className="flex-1 btn-main py-4 rounded-xl font-bold">
                  دفع يدوي
                </button>
                <button onClick={() => setStep(3)} className="flex-1 bg-green-600 hover:bg-green-700 text-white py-4 rounded-xl font-bold">
                  لدي كود سنتر
                </button>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-center">اختر طريقة الدفع</h3>

            <div className="space-y-4">
              <div
                className={`p-4 rounded-xl border-2 cursor-pointer transition ${
                  paymentMethod === 'vodafone'
                    ? 'border-green-500 bg-green-500/10'
                    : 'border-white/10 hover:border-green-500/50'
                }`}
                onClick={() => setPaymentMethod('vodafone')}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === 'vodafone' ? 'border-green-500 bg-green-500' : 'border-white/30'
                  }`}>
                    {paymentMethod === 'vodafone' && '✓'}
                  </div>
                  <GlassIcon name="smartphone" size={24} className="text-green-500" />
                  <div>
                    <h4 className="font-bold">فودافون كاش</h4>
                    <p className="text-sm text-slate-400">رقم المحفظة: {vodafoneCashNumber}</p>
                  </div>
                </div>
              </div>

              <div
                className={`p-4 rounded-xl border-2 cursor-pointer transition ${
                  paymentMethod === 'instapay'
                    ? 'border-blue-500 bg-blue-500/10'
                    : 'border-white/10 hover:border-blue-500/50'
                }`}
                onClick={() => setPaymentMethod('instapay')}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                    paymentMethod === 'instapay' ? 'border-blue-500 bg-blue-500' : 'border-white/30'
                  }`}>
                    {paymentMethod === 'instapay' && '✓'}
                  </div>
                  <GlassIcon name="send" size={24} className="text-blue-500" />
                  <div>
                    <h4 className="font-bold">إنستا باي</h4>
                    <p className={`text-sm ${instapayUsername === 'قريباً' ? 'text-amber-400 font-bold' : 'text-slate-400'}`}>اليوزر نيم: {instapayUsername}</p>
                  </div>
                </div>
              </div>
            </div>

            {paymentMethod === 'vodafone' && (
              <div className="bg-white/5 p-4 rounded-xl text-sm text-slate-300">
                <p>قم بتحويل المبلغ إلى الرقم الموضح أعلاه.</p>
                <p>بعد الضغط على زر الدفع، سيتم تحويلك إلى واتساب لإرسال صورة إيصال التحويل مع رقم عمليتك.</p>
              </div>
            )}

            {paymentMethod === 'instapay' && (
              <div className="bg-white/5 p-4 rounded-xl text-sm text-slate-300">
                <p>قم بتحويل المبلغ إلى حساب إنستا باي الموضح أعلاه.</p>
                <p>بعد الضغط على زر الدفع، سيتم تحويلك إلى واتساب لإرسال صورة إيصال التحويل مع رقم عمليتك.</p>
              </div>
            )}

            <div className="bg-yellow-500/10 p-4 rounded-xl text-sm text-yellow-200">
              <p>⚠️ بعد إرسال الطلب، انتظر موافقة الإدارة ليتم تفعيل الكورس</p>
            </div>

            <button
              onClick={handlePaymentSubmit}
              disabled={loading}
              className="btn-main w-full py-4 rounded-xl font-bold flex justify-center items-center gap-2 disabled:opacity-50"
            >
              {loading ? 'جاري التحويل...' : 'الانتقال لواتساب لإتمام الدفع وإرسال الإيصال'}
              <GlassIcon name="external-link" size={18} />
            </button>

            <button onClick={() => setStep(1)} className="w-full py-3 bg-white/10 rounded-xl font-bold">
              رجوع
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <h3 className="text-2xl font-bold text-center">أدخل كود السنتر</h3>

            <div className="bg-blue-500/10 p-4 rounded-xl text-center">
              <GlassIcon name="gift" size={32} className="mx-auto mb-2 text-blue-400" />
              <p className="text-sm text-slate-300">إذا كان لديك كود مخفض من السنتر، أدخله هنا</p>
            </div>

            <input
              type="text"
              value={centerCode}
              onChange={(e) => setCenterCode(e.target.value.toUpperCase())}
              placeholder="مثال: C123ABC456"
              className="w-full p-4 bg-white/5 border border-white/10 rounded-xl text-center text-2xl tracking-widest"
              maxLength={12}
            />

            {codeError && <p className="text-red-400 text-sm text-center">{codeError}</p>}

            <button
              onClick={handleCodeSubmit}
              disabled={codeValidating}
              className="btn-main w-full py-4 rounded-xl font-bold disabled:opacity-50"
            >
              {codeValidating ? 'جاري التحقق...' : 'تفعيل الكود'}
            </button>

            <button onClick={() => setStep(1)} className="w-full py-3 bg-white/10 rounded-xl font-bold">
              رجوع
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
